<div class="page parent-profile-page">
  <header class="profile-topbar">
    <nav class="breadcrumb" [attr.aria-label]="'COMMON.BREADCRUMB' | translate">
      <a [routerLink]="parentsLink" class="breadcrumb-link">{{ 'PARENTS.TITLE' | translate }}</a>
      <mat-icon class="breadcrumb-separator">chevron_right</mat-icon>
      <span class="breadcrumb-current">{{ nameAr || ('PARENTS.PROFILE' | translate) }}</span>
    </nav>

    <div class="topbar-actions">
      <button type="button" class="btn btn-secondary" (click)="goBack()">
        <mat-icon>arrow_back</mat-icon>
        {{ 'COMMON.BACK' | translate }}
      </button>
      <ng-container *appAccess="'parents:edit'">
        <button type="button" class="btn btn-primary" *ngIf="parent" (click)="onEdit()">
          <mat-icon>edit</mat-icon>
          {{ 'COMMON.EDIT' | translate }}
        </button>
      </ng-container>
    </div>
  </header>

  <div class="alert alert-error" *ngIf="errorMessage">{{ errorMessage | translate }}</div>

  <div class="profile-loading" *ngIf="loading">
    <div class="skeleton-photo"></div>
    <div class="skeleton-copy">
      <span class="skeleton-line wide"></span>
      <span class="skeleton-line"></span>
      <span class="skeleton-line short"></span>
    </div>
  </div>

  <div class="parent-profile" *ngIf="!loading && parent">
    <section class="identity-card">
      <div class="avatar" aria-hidden="true">
        <span class="avatar-fallback">{{ initials }}</span>
      </div>

      <div class="identity-copy">
        <div class="identity-meta">
          <span class="status-pill">
            <span class="status-dot"></span>
            {{ 'PARENTS.PROFILE' | translate }}
          </span>
          <span class="meta-sep" *ngIf="students.length">·</span>
          <span class="meta-note" *ngIf="students.length">
            {{ students.length }} {{ 'PARENTS.STUDENTS' | translate }}
          </span>
        </div>

        <h1 class="name-ar">{{ nameAr }}</h1>
        <p class="name-en" *ngIf="nameEn">{{ nameEn }}</p>

        <ul class="identity-tags">
          <li *ngIf="primaryPhone">
            <mat-icon>call</mat-icon>
            {{ primaryPhone }}
          </li>
          <li *ngIf="primaryEmail">
            <mat-icon>mail</mat-icon>
            {{ primaryEmail }}
          </li>
          <li *ngIf="emergencyCount">
            <mat-icon>emergency</mat-icon>
            {{ 'PARENTS.EMERGENCY_FOR' | translate: { count: emergencyCount } }}
          </li>
        </ul>
      </div>

      <aside class="parent-qr" *ngIf="qrDataUrl">
        <img [src]="qrDataUrl" [alt]="'PARENTS.QR_CODE' | translate" />
      </aside>
    </section>

    <section class="metric-strip" [attr.aria-label]="'PARENTS.PROFILE' | translate">
      <div class="metric">
        <span class="metric-label">{{ 'PARENTS.STUDENTS' | translate }}</span>
        <strong>{{ students.length }}</strong>
        <small>{{ valueOrDash(students[0] ? studentNameAr(students[0]) : '') }}</small>
      </div>
      <div class="metric">
        <span class="metric-label">{{ 'PARENTS.PHONES' | translate }}</span>
        <strong>{{ valueOrDash(primaryPhone) }}</strong>
        <small>{{ phones.length || 0 }} {{ 'PARENTS.PHONES' | translate }}</small>
      </div>
      <div class="metric">
        <span class="metric-label">{{ 'STUDENTS.SECTION_EMAILS' | translate }}</span>
        <strong>{{ valueOrDash(primaryEmail) }}</strong>
        <small>{{ emails.length || 0 }} {{ 'STUDENTS.SECTION_EMAILS' | translate }}</small>
      </div>
      <div class="metric">
        <span class="metric-label">{{ 'STUDENTS.PRIMARY_EMERGENCY' | translate }}</span>
        <strong>{{ emergencyCount }}</strong>
        <small>{{ 'PARENTS.EMERGENCY_FOR' | translate: { count: emergencyCount } }}</small>
      </div>
    </section>

    <div class="profile-body">
      <section class="panel panel-wide">
        <header class="panel-head">
          <h2>{{ 'PARENTS.SECTION_IDENTITY' | translate }}</h2>
        </header>
        <dl class="fields">
          <div class="field">
            <dt>{{ 'STUDENTS.FIRST_NAME_AR' | translate }}</dt>
            <dd>{{ valueOrDash(parent.firstNameAr) }}</dd>
          </div>
          <div class="field">
            <dt>{{ 'STUDENTS.LAST_NAME_AR' | translate }}</dt>
            <dd>{{ valueOrDash(parent.lastNameAr) }}</dd>
          </div>
          <div class="field">
            <dt>{{ 'STUDENTS.FIRST_NAME_EN' | translate }}</dt>
            <dd>{{ valueOrDash(parent.firstNameEn) }}</dd>
          </div>
          <div class="field">
            <dt>{{ 'STUDENTS.LAST_NAME_EN' | translate }}</dt>
            <dd>{{ valueOrDash(parent.lastNameEn) }}</dd>
          </div>
        </dl>
      </section>

      <section class="panel">
        <header class="panel-head">
          <h2>{{ 'PARENTS.SECTION_PHONES' | translate }}</h2>
        </header>
        <div class="contact-stack" *ngIf="phones.length; else noPhones">
          <a class="contact-line" *ngFor="let phone of phones" [href]="'tel:' + phone.value">
            <mat-icon>smartphone</mat-icon>
            <span>
              <strong>{{ phone.value }}</strong>
              <small>
                {{ labelKey(phone.label) | translate }}
                <ng-container *ngIf="phone.isPrimary"> · {{ 'STUDENTS.PRIMARY' | translate }}</ng-container>
              </small>
            </span>
          </a>
        </div>
        <ng-template #noPhones>
          <p class="empty">{{ 'PARENTS.NO_PHONES' | translate }}</p>
        </ng-template>
      </section>

      <section class="panel">
        <header class="panel-head">
          <h2>{{ 'PARENTS.SECTION_EMAILS' | translate }}</h2>
        </header>
        <div class="contact-stack" *ngIf="emails.length; else noEmails">
          <a class="contact-line" *ngFor="let email of emails" [href]="'mailto:' + email.value">
            <mat-icon>alternate_email</mat-icon>
            <span>
              <strong>{{ email.value }}</strong>
              <small>
                {{ labelKey(email.label) | translate }}
                <ng-container *ngIf="email.isPrimary"> · {{ 'STUDENTS.PRIMARY' | translate }}</ng-container>
              </small>
            </span>
          </a>
        </div>
        <ng-template #noEmails>
          <p class="empty">{{ 'PARENTS.NO_EMAILS' | translate }}</p>
        </ng-template>
      </section>

      <section class="panel panel-wide">
        <header class="panel-head">
          <h2>{{ 'PARENTS.SECTION_STUDENTS' | translate }}</h2>
          <span class="count" *ngIf="students.length">{{ students.length }}</span>
        </header>

        <div class="people-list" *ngIf="students.length; else noStudents">
          <article
            class="person-row"
            [class.clickable]="permissions.canView('students')"
            *ngFor="let student of students"
            [class.is-emergency]="student.isPrimaryEmergency"
            (click)="openStudent(student)"
          >
            <div class="person-avatar" aria-hidden="true">{{ studentInitial(student) }}</div>
            <div class="person-copy">
              <h3>{{ studentNameAr(student) }}</h3>
              <p class="person-en" *ngIf="studentNameEn(student)">{{ studentNameEn(student) }}</p>
              <p class="person-role">
                {{ relationKey(student.relation) | translate }}
                <span *ngIf="student.studentCode"> · {{ student.studentCode }}</span>
                <span *ngIf="student.class?.name"> · {{ student.class.name }}</span>
                <span *ngIf="student.isPrimaryEmergency"> · {{ 'STUDENTS.PRIMARY_EMERGENCY' | translate }}</span>
              </p>
            </div>
            <mat-icon class="row-chevron">chevron_right</mat-icon>
          </article>
        </div>
        <ng-template #noStudents>
          <p class="empty">{{ 'PARENTS.NO_STUDENTS' | translate }}</p>
        </ng-template>
      </section>
    </div>
  </div>
</div>
